<template>
  <div class="desktop-editor-component">
    <!-- Desktop Menu -->
    <div class="desktop-editor-menu">
      <div class="menu-bar">
        <div class="menu-group">
          <!-- File Menu -->
          <div class="menu-item"
               @mouseenter="openSubmenu('file')"
               @mouseleave="closeSubmenu('file')"
               :class="{ 'active': activeMenu === 'file' }">
            <span class="menu-label">{{$t('File')}}</span>
            <div class="submenu" v-show="activeMenu === 'file'">
              <div class="submenu-item" @click="handleMenuAction('open')">
                <i class="fas fa-folder"></i>
                <span>{{$t('Open')}}</span>
              </div>
              <div class="submenu-item" @click="handleMenuAction('save')">
                <i class="fas fa-save"></i>
                <span>{{$t('Save')}}</span>
              </div>
              <div class="submenu-item" @click="handleMenuAction('view-as-checklist')">
                <i class="fas fa-clipboard-list"></i>
                <span>{{$t('View')}}</span>
              </div>
              <div class="submenu-item" @click="handleMenuAction('view-as-slideshow')">
                <i class="fas fa-clipboard"></i>
                <span>{{$t('ViewAsSlideshow')}}</span>
              </div>
              <div class="submenu-item" @click="handleMenuAction('import-file')">
                <i class="fas fa-file-import"></i>
                <span>{{$t('Import')}}</span>
              </div>
              <div class="submenu-item" @click="handleMenuAction('export-for-tcvelik')">
                <i class="fas fa-file-export"></i>
                <span>{{$t('Export')}}</span>
              </div>
              <div class="submenu-item has-submenu"
                   @mouseenter="openSubSubmenu('export')"
                   @mouseleave="closeSubSubmenu('export')"
                   :class="{ 'active': activeSubSubmenu === 'export' }">
                <i class="fas fa-file-export"></i>
                <span>{{$t('ExportAs')}}</span>
                <i class="fas fa-chevron-right submenu-arrow"></i>
                <div class="sub-submenu" v-show="activeSubSubmenu === 'export'">
                  <div class="submenu-item" @click="handleMenuAction('export-to-pptx')">
                    <i class="fas fa-file-export"></i>
                    <span>{{$t('DownloadAsPPTX')}}</span>
                  </div>
                  <div class="submenu-item" @click="handleMenuAction('export-sop-format')">
                    <i class="fas fa-file-export"></i>
                    <span>{{$t('TCVelikSOPFormat')}}</span>
                  </div>
                </div>
              </div>
              <div class="submenu-divider"></div>
              <div class="submenu-item" @click="handleMenuAction('share')">
                <i class="fas fa-share-alt"></i>
                <span>{{$t('Share')}}</span>
              </div>
              <div class="submenu-item" @click="handleMenuAction('add-space')">
                <i class="fas fa-plus-square"></i>
                <span>{{$t('AddToSpace')}}</span>
              </div>
              <div class="submenu-item" @click="handleMenuAction('open-review-menu')">
                <i class="fas fa-user-group"></i>
                <span>{{$t('OpenReviewMenu')}}</span>
              </div>
              <div class="submenu-item" @click="handleMenuAction('open-complete-sop-menu')">
                <i class="fas fa-tasks"></i>
                <span>Open Complete SOP Menu</span>
              </div>
              <div class="submenu-item" @click="handleMenuAction('generate-preview-link')">
                <i class="fas fa-share-alt"></i>
                <span>{{$t('GeneratePreviewLink')}}</span>
              </div>
              <div class="submenu-divider"></div>
              <div class="submenu-item danger" @click="handleMenuAction('delete')">
                <i class="fa fa-trash"></i>
                <span>{{$t('Delete')}}</span>
              </div>
              <div class="submenu-item" @click="handleMenuAction('clear-all')">
                <i class="fas fa-times"></i>
                <span>{{$t('Close')}}</span>
              </div>
            </div>
          </div>

          <!-- Edit Menu -->
          <div class="menu-item"
               @mouseenter="openSubmenu('edit')"
               @mouseleave="closeSubmenu('edit')"
               :class="{ 'active': activeMenu === 'edit' }">
            <span class="menu-label">{{$t('Edit')}}</span>
            <div class="submenu" v-show="activeMenu === 'edit'">
              <div class="submenu-item"
                   @click="handleEditorCommand('undo')"
                   :class="{ 'disabled': !editor.can().undo() }">
                <i class="fas fa-undo"></i>
                <span>{{$t('Undo')}}</span>
              </div>
              <div class="submenu-item"
                   @click="handleEditorCommand('redo')"
                   :class="{ 'disabled': !editor.can().redo() }">
                <i class="fas fa-redo"></i>
                <span>{{$t('Redo')}}</span>
              </div>
              <div class="submenu-divider"></div>
              <div class="submenu-item" @click="handleEditorCommand('selectAll')">
                <i class="fas fa-object-group"></i>
                <span>{{$t('SelectAll')}}</span>
              </div>
            </div>
          </div>

          <!-- View Menu -->
          <div class="menu-item"
               @mouseenter="openSubmenu('view')"
               @mouseleave="closeSubmenu('view')"
               :class="{ 'active': activeMenu === 'view' }">
            <span class="menu-label">{{$t('View')}}</span>
            <div class="submenu" v-show="activeMenu === 'view'">
              <div class="submenu-item" @click="handleMenuAction('open-html-edit', editor.getHTML())">
                <i class="fas fa-code"></i>
                <span>{{$t('HTMLSource')}}</span>
              </div>
            </div>
          </div>

          <!-- Tools Menu -->
          <div class="menu-item"
               @mouseenter="openSubmenu('tools')"
               @mouseleave="closeSubmenu('tools')"
               :class="{ 'active': activeMenu === 'tools' }">
            <span class="menu-label">{{$t('Tools')}}</span>
            <div class="submenu" v-show="activeMenu === 'tools'">
              <div class="submenu-item" @click="handleMenuAction('properties')">
                <i class="fas fa-cog"></i>
                <span>{{$t('Properties')}}</span>
              </div>
              <div class="submenu-item" @click="handleMenuAction('assign')">
                <i class="fas fa-user"></i>
                <span>{{$t('Assign')}}</span>
              </div>
              <div class="submenu-item" @click="handleMenuAction('assign-role')">
                <i class="fas fa-users"></i>
                <span>{{$t('AssignToRole')}}</span>
              </div>
              <div class="submenu-item" @click="handleMenuAction('open-intent-menu')">
                <i class="fas fa-list-ul"></i>
                <span>{{$t('EditIntent')}}</span>
              </div>
            </div>
          </div>

          <div class="menu-item"
               :class="{ 'active': activeMenu === 'exit' }" @click="closeEditor">
            <span class="menu-label">{{$t('ExitEditor')}}</span>
          </div>
        </div>

        <div class="menu-group">
          <strong>{{ title }}</strong>
        </div>
      </div>
    </div>

    <!-- Mobile Menu Trigger -->
    <div class="mobile-menu-trigger" @click="openMobileMenu">
      <i class="fas fa-bars"></i>
    </div>

    <!-- Mobile Menu Panel -->
    <div v-if="isMobileMenuOpen" class="mobile-menu-overlay" @click="closeMobileMenu"></div>
    <div class="mobile-menu-panel" :class="{ 'is-open': isMobileMenuOpen }">
      <div class="mobile-menu-header">
        <h3>{{$t('Menu')}}</h3>
        <button @click="closeMobileMenu" class="mobile-close-button">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="mobile-menu-content">
        <!-- File Menu -->
        <div class="mobile-menu-section">
          <div class="mobile-menu-title" @click="toggleMobileSubmenu('file')">
            <i class="fas fa-file"></i>
            <span>{{$t('File')}}</span>
            <i class="fas fa-chevron-down" :class="{ 'rotated': activeMobileSubmenu === 'file' }"></i>
          </div>
          <div class="mobile-submenu" v-show="activeMobileSubmenu === 'file'">
            <div class="mobile-menu-item" @click="handleMenuAction('open')">
              <i class="fas fa-folder"></i>
              <span>{{$t('Open')}}</span>
            </div>
            <div class="mobile-menu-item" @click="handleMenuAction('save')">
              <i class="fas fa-save"></i>
              <span>{{$t('Save')}}</span>
            </div>
            <div class="mobile-menu-item" @click="handleMenuAction('import-file')">
              <i class="fas fa-file-import"></i>
              <span>{{$t('Import')}}</span>
            </div>
            <div class="mobile-menu-item" @click="handleMenuAction('export-for-tcvelik')">
              <i class="fas fa-file-export"></i>
              <span>{{$t('Export')}}</span>
            </div>

            <!-- Export As Submenu -->
            <div class="mobile-menu-item has-submenu" @click="toggleMobileSubSubmenu('export')">
              <i class="fas fa-file-export"></i>
              <span>{{$t('ExportAs')}}</span>
              <i class="fas fa-chevron-right" :class="{ 'rotated': activeMobileSubSubmenu === 'export' }"></i>
            </div>
            <div class="mobile-sub-submenu" v-show="activeMobileSubSubmenu === 'export'">
              <div class="mobile-menu-item" @click="handleMenuAction('export-to-pptx')">
                <i class="fas fa-file-export"></i>
                <span>{{$t('DownloadAsPPTX')}}</span>
              </div>
              <div class="mobile-menu-item" @click="handleMenuAction('export-sop-format')">
                <i class="fas fa-file-export"></i>
                <span>{{$t('TCVelikSOPFormat')}}</span>
              </div>
            </div>

            <div class="mobile-menu-divider"></div>
            <div class="mobile-menu-item" @click="handleMenuAction('share')">
              <i class="fas fa-share-alt"></i>
              <span>{{$t('Share')}}</span>
            </div>
            <div class="mobile-menu-item" @click="handleMenuAction('add-space')">
              <i class="fas fa-plus-square"></i>
              <span>{{$t('AddToSpace')}}</span>
            </div>
            <div class="mobile-menu-item" @click="handleMenuAction('open-review-menu')">
              <i class="fas fa-user-group"></i>
              <span>{{$t('OpenReviewMenu')}}</span>
            </div>
            <div class="mobile-menu-item" @click="handleMenuAction('open-complete-sop-menu')">
              <i class="fas fa-tasks"></i>
              <span>Open Complete SOP Menu</span>
            </div>
            <div class="mobile-menu-item" @click="handleMenuAction('generate-preview-link')">
              <i class="fas fa-share-alt"></i>
              <span>{{$t('GeneratePreviewLink')}}</span>
            </div>
            <div class="mobile-menu-divider"></div>
            <div class="mobile-menu-item danger" @click="handleMenuAction('delete')">
              <i class="fa fa-trash"></i>
              <span>{{$t('Delete')}}</span>
            </div>
            <div class="mobile-menu-item" @click="handleMenuAction('clear-all')">
              <i class="fas fa-times"></i>
              <span>{{$t('Close')}}</span>
            </div>
          </div>
        </div>

        <!-- Edit Menu -->
        <div class="mobile-menu-section">
          <div class="mobile-menu-title" @click="toggleMobileSubmenu('edit')">
            <i class="fas fa-edit"></i>
            <span>{{$t('Edit')}}</span>
            <i class="fas fa-chevron-down" :class="{ 'rotated': activeMobileSubmenu === 'edit' }"></i>
          </div>
          <div class="mobile-submenu" v-show="activeMobileSubmenu === 'edit'">
            <div class="mobile-menu-item"
                 @click="handleEditorCommand('undo')"
                 :class="{ 'disabled': !editor.can().undo() }">
              <i class="fas fa-undo"></i>
              <span>{{$t('Undo')}}</span>
            </div>
            <div class="mobile-menu-item"
                 @click="handleEditorCommand('redo')"
                 :class="{ 'disabled': !editor.can().redo() }">
              <i class="fas fa-redo"></i>
              <span>{{$t('Redo')}}</span>
            </div>
            <div class="mobile-menu-divider"></div>
            <div class="mobile-menu-item" @click="handleEditorCommand('selectAll')">
              <i class="fas fa-object-group"></i>
              <span>{{$t('SelectAll')}}</span>
            </div>
          </div>
        </div>

        <!-- View Menu -->
        <div class="mobile-menu-section">
          <div class="mobile-menu-title" @click="toggleMobileSubmenu('view')">
            <i class="fas fa-eye"></i>
            <span>{{$t('View')}}</span>
            <i class="fas fa-chevron-down" :class="{ 'rotated': activeMobileSubmenu === 'view' }"></i>
          </div>
          <div class="mobile-submenu" v-show="activeMobileSubmenu === 'view'">
            <div class="mobile-menu-item" @click="handleMenuAction('open-html-edit', editor.getHTML())">
              <i class="fas fa-code"></i>
              <span>{{$t('HTMLSource')}}</span>
            </div>
          </div>
        </div>

        <!-- Tools Menu -->
        <div class="mobile-menu-section">
          <div class="mobile-menu-title" @click="toggleMobileSubmenu('tools')">
            <i class="fas fa-tools"></i>
            <span>{{$t('Tools')}}</span>
            <i class="fas fa-chevron-down" :class="{ 'rotated': activeMobileSubmenu === 'tools' }"></i>
          </div>
          <div class="mobile-submenu" v-show="activeMobileSubmenu === 'tools'">
            <div class="mobile-menu-item" @click="handleMenuAction('properties')">
              <i class="fas fa-cog"></i>
              <span>{{$t('Properties')}}</span>
            </div>
            <div class="mobile-menu-item" @click="handleMenuAction('assign')">
              <i class="fas fa-user"></i>
              <span>{{$t('Assign')}}</span>
            </div>
            <div class="mobile-menu-item" @click="handleMenuAction('assign-role')">
              <i class="fas fa-users"></i>
              <span>{{$t('AssignToRole')}}</span>
            </div>
            <div class="mobile-menu-item" @click="handleMenuAction('open-intent-menu')">
              <i class="fas fa-list-ul"></i>
              <span>{{$t('EditIntent')}}</span>
            </div>
          </div>
        </div>

        <div class="mobile-menu-section">
          <div class="mobile-menu-title" @click="closeEditor">
            <i class="fas fa-xmark"></i>
            <span>{{$t('ExitEditor')}}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { EventBus } from '@/eventBus/eventBus.js';

export default {
  name: 'SOPEditorMenu',
  props: {
    editor: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      title: "New onboarding (not saved)",
      activeMenu: null,
      activeSubSubmenu: null,
      submenuTimeout: null,
      subSubmenuTimeout: null,
      isMobileMenuOpen: false,
      activeMobileSubmenu: null,
      activeMobileSubSubmenu: null
    }
  },
  mounted() {
    EventBus.$on(`onboarding_editor_title_change`, (title) => {
      this.title = title
    });
    
    // Close submenus when clicking outside
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.submenuTimeout) {
      clearTimeout(this.submenuTimeout);
    }
    if (this.subSubmenuTimeout) {
      clearTimeout(this.subSubmenuTimeout);
    }
  },
  methods: {
    openSubmenu(menuName) {
      if (this.submenuTimeout) {
        clearTimeout(this.submenuTimeout);
      }
      this.activeMenu = menuName;
    },

    closeSubmenu(menuName) {
      this.submenuTimeout = setTimeout(() => {
        if (this.activeMenu === menuName) {
          this.activeMenu = null;
        }
      }, 150); // Small delay to prevent flickering
    },

    openSubSubmenu(submenuName) {
      if (this.subSubmenuTimeout) {
        clearTimeout(this.subSubmenuTimeout);
      }
      this.activeSubSubmenu = submenuName;
    },

    closeSubSubmenu(submenuName) {
      this.subSubmenuTimeout = setTimeout(() => {
        if (this.activeSubSubmenu === submenuName) {
          this.activeSubSubmenu = null;
        }
      }, 150); // Small delay to prevent flickering
    },

    handleClickOutside(event) {
      if (!event.target.closest('.menu-item')) {
        this.activeMenu = null;
      }
    },

    handleMenuAction(action, payload = null) {
      this.$emit(action, payload);
      this.activeMenu = null;
    },

    handleEditorCommand(command) {
      if (!this.editor) return;
      
      if (command === 'selectAll') {
        this.editor.chain().focus().selectAll().run();
      } else if (command === 'undo') {
        this.editor.chain().focus().undo().run();
      } else if (command === 'redo') {
        this.editor.chain().focus().redo().run();
      }
      
      this.activeMenu = null;
    },

    // Mobile menu methods
    openMobileMenu() {
      this.isMobileMenuOpen = true;
    },

    closeMobileMenu() {
      this.isMobileMenuOpen = false;
      this.activeMobileSubmenu = null;
      this.activeMobileSubSubmenu = null;
    },

    closeEditor() {
        window.location.pathname = '/pages/home';
      
    },

    toggleMobileSubmenu(menuName) {
      if (this.activeMobileSubmenu === menuName) {
        this.activeMobileSubmenu = null;
      } else {
        this.activeMobileSubmenu = menuName;
      }
    },

    toggleMobileSubSubmenu(submenuName) {
      if (this.activeMobileSubSubmenu === submenuName) {
        this.activeMobileSubSubmenu = null;
      } else {
        this.activeMobileSubSubmenu = submenuName;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.desktop-editor-menu {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 0;
  position: relative;
  z-index: 1000;
}

.menu-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  height: 40px;
}

.menu-group {
  display: flex;
  align-items: center;
  gap: 0;
}

.menu-item {
  position: relative;
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  user-select: none;

  &:hover {
    background-color: #e9ecef;
  }

  &.active {
    background-color: #007bff;
    color: white;
  }

  .menu-label {
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
  }
}

.submenu {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 200px;
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1100;
  padding: 4px 0;
  margin-top: 2px;
}

.submenu-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 14px;
  color: #333;
  white-space: nowrap;

  &:hover {
    background-color: #f8f9fa;
  }

  &.disabled {
    color: #6c757d;
    cursor: not-allowed;
    
    &:hover {
      background-color: transparent;
    }
  }

  &.danger {
    color: #dc3545;
    
    &:hover {
      background-color: #f8d7da;
    }
  }

  &.has-submenu {
    position: relative;
    
    .submenu-arrow {
      margin-left: auto;
      font-size: 12px;
      color: #6c757d;
    }
    
    &.active {
      background-color: #f8f9fa;
    }
  }

  i {
    width: 16px;
    margin-right: 8px;
    text-align: center;
  }

  span {
    flex: 1;
  }
}

.sub-submenu {
  position: absolute;
  top: 0;
  left: 100%;
  min-width: 200px;
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1200;
  padding: 4px 0;
  margin-left: 2px;
  animation: fadeInLeft 0.2s ease-out;
}

@keyframes fadeInLeft {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.submenu-divider {
  height: 1px;
  background-color: #dee2e6;
  margin: 4px 0;
}

// Animation for submenu appearance
.submenu {
  animation: fadeInUp 0.2s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .desktop-editor-menu {
    display: none;
  }
}

// Mobile menu styles
.mobile-menu-trigger {
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  position: fixed;
  top: 10px;
  right: 10px;
  z-index: 1300;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: #0056b3;
  }

  i {
    font-size: 18px;
  }
}

.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1400;
}

.mobile-menu-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 300px;
  height: 100%;
  background-color: white;
  z-index: 1500;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;

  &.is-open {
    transform: translateX(0);
  }
}

.mobile-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;

  h3 {
    margin: 0;
    font-size: 1.2rem;
    color: #333;
  }

  .mobile-close-button {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #6c757d;
    padding: 0.25rem;
    border-radius: 4px;

    &:hover {
      background-color: #e9ecef;
    }
  }
}

.mobile-menu-content {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.mobile-menu-section {
  margin-bottom: 0.5rem;
}

.mobile-menu-title {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-weight: 500;
  color: #333;

  &:hover {
    background-color: #e9ecef;
  }

  i:first-child {
    width: 20px;
    margin-right: 0.75rem;
    text-align: center;
  }

  span {
    flex: 1;
  }

  i:last-child {
    transition: transform 0.2s;
    
    &.rotated {
      transform: rotate(180deg);
    }
  }
}

.mobile-submenu {
  margin-left: 1rem;
  margin-top: 0.25rem;
  border-left: 2px solid #dee2e6;
  padding-left: 0.5rem;
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  border-radius: 4px;
  margin-bottom: 0.25rem;
  color: #333;

  &:hover {
    background-color: #f8f9fa;
  }

  &.disabled {
    color: #6c757d;
    cursor: not-allowed;
    
    &:hover {
      background-color: transparent;
    }
  }

  &.danger {
    color: #dc3545;
    
    &:hover {
      background-color: #f8d7da;
    }
  }

  &.has-submenu {
    position: relative;
    
    i:last-child {
      transition: transform 0.2s;
      
      &.rotated {
        transform: rotate(90deg);
      }
    }
  }

  i:first-child {
    width: 20px;
    margin-right: 0.75rem;
    text-align: center;
  }

  span {
    flex: 1;
  }
}

.mobile-sub-submenu {
  margin-left: 1rem;
  margin-top: 0.25rem;
  border-left: 2px solid #e9ecef;
  padding-left: 0.5rem;
}

.mobile-menu-divider {
  height: 1px;
  background-color: #dee2e6;
  margin: 0.5rem 0;
}

@media (max-width: 768px) {
  .mobile-menu-trigger {
    display: flex;
  }
}
</style>
